import React from 'react'
import styled, { css } from 'styled-components'
import theme from 'styled-theming'
import { transparentize } from 'polished'

import NavLink from '../NavLink'
import {
  COLORS,
  CSS_ELEMENTS_RESETS,
  focusWithIn,
  hover,
  focus,
  media,
  typeScale,
  THEME,
  dropShadow,
} from '../../styles'
import PricePerPortionBadge from '../PricePerPortionBadge'

const DIRECTION = {
  top: theme('direction', {
    vertical: '6rem',
    horizontal: '0',
  }),
  topMedium: theme('direction', {
    vertical: '8rem',
    horizontal: '0',
  }),
  imageWidth: theme('direction', {
    vertical: '100%',
    horizontal: '9.8rem',
  }),
  imagePaddingTop: theme('direction', {
    vertical: '100%',
    horizontal: 'unset',
  }),
  imageHeight: theme('direction', {
    vertical: 'auto',
    horizontal: '9.8rem',
  }),
  content: theme('direction', {
    vertical:
      'display: flex; flex-flow: column nowrap; align-items: center; justify-content: center; height: 100%;',
    horizontal:
      'display: flex; flex-flow: row nowrap; align-items: center; max-height: 9.8rem; overflow: hidden;',
  }),
  tags: theme('direction', {
    vertical: 'padding-top: 0;',
    horizontal: 'display: none;',
  }),
  text: theme('direction', {
    vertical:
      'display: flex; flex-flow: column nowrap; align-items: center; justify-content: center; flex: 1;',
    horizontal: '',
  }),
  textMargin: theme('direction', {
    vertical: '-1.6rem 0 0 0',
    horizontal: '0',
  }),
  textMarginMedium: theme('direction', {
    vertical: '-1.6rem 0 0 0',
    horizontal: '0',
  }),
  textAlign: theme('direction', {
    vertical: 'center',
    horizontal: 'left',
  }),
  textPadding: theme('direction', {
    vertical: '0.8rem 1.2rem 1.2rem',
    horizontal: '1.2rem 1.2rem 1.2rem 0',
  }),
  textPaddingMedium: theme('direction', {
    vertical: '0.8rem 0.8rem 1.2rem',
    horizontal: '2.4rem 2.4rem 2.4rem 0',
  }),
}

const VARIANTS = {
  border: theme('variant', {
    main: '0',
    drawer: `0.1rem solid ${THEME.COLORS.foreground.tertiary}`,
    cart: `0.1rem solid ${THEME.COLORS.foreground.tertiary}`,
    embed: `0.1rem solid ${THEME.COLORS.foreground.tertiary}`,
  }),
}

const Styled = styled.article`
  min-height: 100%;
  padding-top: ${DIRECTION.top};
  width: 100%;
  position: relative;
  ${media.medium`
    padding-top: ${DIRECTION.topMedium};
  `}
`
Styled.thumbnailContainer = styled.div`
  margin-top: -${DIRECTION.top};
  padding-top: ${DIRECTION.imagePaddingTop};
  position: relative;
  transition: transform 0.2s ease-in-out;
  width: ${DIRECTION.imageWidth};
  height: ${DIRECTION.imageHeight};
  ${media.medium`
    margin-top: -${DIRECTION.topMedium};
  `}
`
Styled.thumbnail = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  display: block;
  transition-property: opacity;
  transition-duration: 0.2s;
`
Styled.thumbnailWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  opacity: ${({ isCooked }) => (isCooked ? '0.5' : '1')};
  transition: opacity 0.2s ease-in-out;

  img {
    ${({ imageLoaded }) =>
      dropShadow(
        {
          offsetX: '0.4rem',
          offsetY: '0.8rem',
          color: transparentize(0.4, COLORS.black),
        },
        imageLoaded,
      )};
  }
`
Styled.title = styled.p`
  ${typeScale('body')};
  color: ${({ isCooked }) =>
    isCooked
      ? THEME.COLORS.foreground.secondary
      : THEME.COLORS.foreground.primary};
  margin: 0;
  transition:
    color 0.2s ease-in-out,
    text-decoration 0.2s ease-in-out;
  width: 100%;
  text-decoration: ${({ isCooked }) => (isCooked ? 'line-through' : 'none')};
`
Styled.description = styled.p`
  display: none;
  ${media.medium`
    ${typeScale('caption')};
    color: ${THEME.COLORS.foreground.secondary};
    margin: 0;
    padding-top: 0.4rem;
    width: 100%;
    display: block;
  `}
`
Styled.labels = styled.p`
  ${DIRECTION.tags};
`
Styled.badgeEdito = styled.span`
  ${typeScale('caption')};
  padding: 0.3rem 0.6rem;
  border: 0;
  background-color: ${COLORS.radish};
  color: ${COLORS.beet};
  border-radius: 9999px;
  display: inline-block;
  white-space: nowrap;
  margin: 0.4rem 0.2rem 0;
`
Styled.badgeUnit = styled(Styled.badgeEdito)`
  background-color: ${COLORS.powder};
  color: ${COLORS.black};
`
Styled.text = styled.div`
  ${DIRECTION.text};
  color: ${THEME.COLORS.foreground.primary};
  margin: ${DIRECTION.textMargin};
  padding: ${DIRECTION.textPadding};
  text-align: ${DIRECTION.textAlign};
  width: 100%;
  ${media.medium`
    padding: ${DIRECTION.textPaddingMedium};
    margin: ${DIRECTION.textMarginMedium};
  `}
  ${({ isLoading }) =>
    isLoading
      ? ''
      : css`
          ${Styled.title} {
            display: -webkit-box;
            overflow: hidden;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 4;
            ${media.medium`
              -webkit-line-clamp: 3;
            `}
          }
          ${Styled.description} {
            display: none;
            ${media.medium`
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              overflow: hidden;
            `}
          }
        `};
`
Styled.content = styled.div`
  margin: 0;
  width: 100%;
  position: relative;
  ${DIRECTION.content}
`
Styled.wrapper = styled(({ to, href, children, isCooked, ...rest }) =>
  href ? (
    <a href={href} {...rest}>
      {children}
    </a>
  ) : to ? (
    <NavLink to={to} {...rest}>
      {children}
    </NavLink>
  ) : (
    <div {...rest}>{children}</div>
  ),
)`
  display: block;
  width: 100%;
  text-decoration: none;
  flex: 1;
  ${({ to, isCooked }) =>
    to && !isCooked
      ? hover`
        ${Styled.title} {
          color: ${COLORS.primary};
        }
      `
      : ''};
  ${focus`
    outline: 0;
  `};
`
Styled.titlePlaceholder = styled.span`
  background-color: ${THEME.COLORS.background.tertiary};
  width: 70%;
  display: inline-block;
  height: 1.6rem;

  &:last-of-type {
    width: 40%;
  }
`
Styled.container = styled.div`
  background-color: ${THEME.COLORS.background.secondary};
  border-radius: 1.6rem;
  border: ${VARIANTS.border};
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
  width: 100%;
  ${({ isCooked }) =>
    isCooked
      ? ''
      : focusWithIn`
    ${Styled.title} {
      color: ${COLORS.primary};
    }
  `}
`
Styled.descriptionPlaceholder = styled(Styled.titlePlaceholder)`
  width: 90%;
  height: 1rem;

  &:last-of-type {
    width: 60%;
  }
`
Styled.missingIngredients = styled.button`
  ${CSS_ELEMENTS_RESETS.button};
  cursor: help;
  margin: 0.8rem auto 0.4rem;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${COLORS.primary};
  transition: color 0.2s ease-in-out;
`
Styled.pricePerPortionBadge = styled(PricePerPortionBadge)`
  position: absolute;
  right: 0.4rem;
  top: 4.4rem;
  ${media.medium`
    top: 6.4rem;
  `}
`

export default Styled
